import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { server, qrBaseUrl } from "../../server";
import { useReactToPrint } from "react-to-print";
import useAuth from "../../useAuth";
import {
  FaPrint,
  FaIdCard,
  FaUserGraduate,
  FaSearch,
  FaSearchMinus,
  FaSearchPlus,
  FaArrowsAlt,
  FaUndo,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
  FaCheckCircle,
  FaGraduationCap,
  FaLayerGroup,
  FaHashtag,
  FaIdBadge,
  FaTimes,
} from "react-icons/fa";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";

export default function GenerateIdCard() {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedSection, setSelectedSection] = useState("ALL");
  const [rollNoInput, setRollNoInput] = useState("");
  const [admissionNoInput, setAdmissionNoInput] = useState("");
  const { token } = useAuth();

  // Debounce Search Term (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${server}/students`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data.data || [];
        setStudents(data);
        if (data.length > 0) {
          setSelectedStudent(data[0]);
        }
      } catch (error) {
        toast.error("Failed to load students");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchStudents();
  }, [token]);

  // Photo adjustment states (Zoom, Position, Drag)
  const [photoScale, setPhotoScale] = useState(1);
  const [photoPos, setPhotoPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setPhotoScale(1);
    setPhotoPos({ x: 0, y: 0 });
  };

  // Drag handlers for photo positioning
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - photoPos.x, y: e.clientY - photoPos.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPhotoPos({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - photoPos.x,
        y: e.touches[0].clientY - photoPos.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    setPhotoPos({
      x: e.touches[0].clientX - dragStart.x,
      y: e.touches[0].clientY - dragStart.y,
    });
  };

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `ID_Card_${selectedStudent?.studentName || "Student"}`,
    pageStyle: `
      @page {
        size: 360px 580px;
        margin: 0;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  // Extract unique classes & sections for filters
  const availableClasses = useMemo(() => {
    return Array.from(
      new Set(students.map((s) => s.class).filter(Boolean))
    ).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return String(a).localeCompare(String(b));
    });
  }, [students]);

  const availableSections = useMemo(() => {
    return Array.from(
      new Set(students.map((s) => s.section).filter(Boolean))
    ).sort();
  }, [students]);

  const classCounts = useMemo(() => {
    const counts = {};
    students.forEach((s) => {
      if (s.class) {
        const clsKey = String(s.class);
        counts[clsKey] = (counts[clsKey] || 0) + 1;
      }
    });
    return counts;
  }, [students]);

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const matchClass =
        selectedClass === "ALL" || String(s.class || "") === String(selectedClass);

      const matchSection =
        selectedSection === "ALL" || String(s.section || "") === String(selectedSection);

      const matchSearch =
        !debouncedSearchTerm ||
        s.studentName?.toLowerCase().includes(debouncedSearchTerm) ||
        String(s.rollNumber || "").toLowerCase().includes(debouncedSearchTerm) ||
        String(s.admissionNumber || "").toLowerCase().includes(debouncedSearchTerm) ||
        s.fatherName?.toLowerCase().includes(debouncedSearchTerm);

      const matchRollNo =
        !rollNoInput.trim() ||
        String(s.rollNumber || "").toLowerCase().includes(rollNoInput.trim().toLowerCase());

      const matchAdmissionNo =
        !admissionNoInput.trim() ||
        String(s.admissionNumber || "").toLowerCase().includes(admissionNoInput.trim().toLowerCase());

      return matchClass && matchSection && matchSearch && matchRollNo && matchAdmissionNo;
    });
  }, [students, debouncedSearchTerm, selectedClass, selectedSection, rollNoInput, admissionNoInput]);

  const hasActiveFilters =
    searchTerm !== "" ||
    selectedClass !== "ALL" ||
    selectedSection !== "ALL" ||
    rollNoInput !== "" ||
    admissionNoInput !== "";

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedClass("ALL");
    setSelectedSection("ALL");
    setRollNoInput("");
    setAdmissionNoInput("");
  };

  const handleNextStudent = () => {
    if (!filteredStudents.length) return;
    const currentIndex = filteredStudents.findIndex((s) => s.id === selectedStudent?.id);
    const nextIndex = (currentIndex + 1) % filteredStudents.length;
    handleStudentSelect(filteredStudents[nextIndex]);
  };

  const handlePrevStudent = () => {
    if (!filteredStudents.length) return;
    const currentIndex = filteredStudents.findIndex((s) => s.id === selectedStudent?.id);
    const prevIndex = (currentIndex - 1 + filteredStudents.length) % filteredStudents.length;
    handleStudentSelect(filteredStudents[prevIndex]);
  };

  const formatDOB = (dobStr) => {
    if (!dobStr || dobStr.startsWith("0001-") || dobStr.startsWith("1900-"))
      return "N/A";
    const dateObj = new Date(dobStr);
    return isNaN(dateObj.getTime())
      ? "N/A"
      : dateObj.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Header Bar (Unchanged Header Title/Subtitle as requested) */}
      <div className="print:hidden bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaIdCard size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
              Student ID Card Generator
            </h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Select a student to design and print their official School Student ID
            </p>
          </div>
        </div>

        {selectedStudent && (
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5 shrink-0"
            >
              <FaPrint size={11} /> Print Official ID Card
            </button>
          </div>
        )}
      </div>

      {/* Main 2-Column Split View: Both wrapped in clean Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* LEFT COLUMN CARD: Filters & Student List */}
        <div className="print:hidden lg:col-span-5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs flex flex-col gap-4">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <FaFilter className="text-indigo-600 dark:text-indigo-400 text-xs" />
              <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider">
                Student Directory & Filters
              </span>
            </div>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="text-[10px] font-extrabold text-rose-500 dark:text-rose-400 hover:underline flex items-center gap-0.5"
                >
                  <FaTimes size={10} /> Reset
                </button>
              )}
              <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded-full">
                {filteredStudents.length} / {students.length}
              </span>
            </div>
          </div>

          {/* Quick Class Selector Pills */}
          {availableClasses.length > 0 && (
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                <FaGraduationCap size={10} /> Quick Class Filter
              </label>
              <div className="flex items-center gap-1 overflow-x-auto pb-1 custom-scrollbar">
                <button
                  type="button"
                  onClick={() => setSelectedClass("ALL")}
                  className={`px-2.5 py-1 rounded-xl text-[11px] font-bold whitespace-nowrap transition-all ${
                    selectedClass === "ALL"
                      ? "bg-indigo-600 text-white shadow-xs"
                      : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                  }`}
                >
                  All ({students.length})
                </button>
                {availableClasses.map((cls) => (
                  <button
                    key={cls}
                    type="button"
                    onClick={() => setSelectedClass(cls)}
                    className={`px-2.5 py-1 rounded-xl text-[11px] font-bold whitespace-nowrap transition-all flex items-center gap-1 ${
                      selectedClass === cls
                        ? "bg-indigo-600 text-white shadow-xs"
                        : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    Class {cls}
                    <span
                      className={`text-[9px] px-1 py-0.2 rounded-full ${
                        selectedClass === cls
                          ? "bg-white/20 text-white"
                          : "bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300"
                      }`}
                    >
                      {classCounts[cls] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 4-Column Filter Grid: Class, Section, Roll No, Reg No */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-gray-50/80 dark:bg-slate-800/40 p-2.5 rounded-2xl border border-gray-200/80 dark:border-slate-800">
            {/* Class Dropdown */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                <FaGraduationCap size={10} /> Class
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:border-indigo-500"
              >
                <option value="ALL">All Classes</option>
                {availableClasses.map((cls) => (
                  <option key={cls} value={cls}>
                    Class {cls}
                  </option>
                ))}
              </select>
            </div>

            {/* Section Dropdown */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                <FaLayerGroup size={10} /> Section
              </label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:border-indigo-500"
              >
                <option value="ALL">All Sections</option>
                {availableSections.map((sec) => (
                  <option key={sec} value={sec}>
                    Section {sec}
                  </option>
                ))}
              </select>
            </div>

            {/* Roll No Input */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                <FaHashtag size={10} /> Roll No
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="e.g. 12"
                  value={rollNoInput}
                  onChange={(e) => setRollNoInput(e.target.value)}
                  className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:border-indigo-500"
                />
                {rollNoInput && (
                  <button
                    type="button"
                    onClick={() => setRollNoInput("")}
                    className="absolute right-1.5 top-1 text-gray-400 hover:text-gray-600 text-[10px]"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>

            {/* Admission No Input */}
            <div>
              <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                <FaIdBadge size={10} /> Reg No
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="e.g. 1024"
                  value={admissionNoInput}
                  onChange={(e) => setAdmissionNoInput(e.target.value)}
                  className="w-full px-2 py-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:border-indigo-500"
                />
                {admissionNoInput && (
                  <button
                    type="button"
                    onClick={() => setAdmissionNoInput("")}
                    className="absolute right-1.5 top-1 text-gray-400 hover:text-gray-600 text-[10px]"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Quick Search Input (Placed Below Dropdowns) */}
          <div>
            <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
              Search Student Name or Details
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-xs" />
              <input
                type="text"
                placeholder="Search by Name, Roll, Reg No, Father..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500 shadow-2xs"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-2 text-xs font-bold text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {/* Active Filter Chips Summary */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-1.5 p-2 bg-indigo-50/60 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900/50 text-[11px]">
              <span className="font-extrabold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                <FaFilter size={9} /> Active:
              </span>
              {selectedClass !== "ALL" && (
                <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                  Class: {selectedClass}
                  <button onClick={() => setSelectedClass("ALL")} className="hover:text-rose-500">
                    <FaTimes size={9} />
                  </button>
                </span>
              )}
              {selectedSection !== "ALL" && (
                <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                  Sec: {selectedSection}
                  <button onClick={() => setSelectedSection("ALL")} className="hover:text-rose-500">
                    <FaTimes size={9} />
                  </button>
                </span>
              )}
              {rollNoInput && (
                <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                  Roll: {rollNoInput}
                  <button onClick={() => setRollNoInput("")} className="hover:text-rose-500">
                    <FaTimes size={9} />
                  </button>
                </span>
              )}
              {admissionNoInput && (
                <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                  Reg: {admissionNoInput}
                  <button onClick={() => setAdmissionNoInput("")} className="hover:text-rose-500">
                    <FaTimes size={9} />
                  </button>
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                  "{searchTerm}"
                  <button onClick={() => setSearchTerm("")} className="hover:text-rose-500">
                    <FaTimes size={9} />
                  </button>
                </span>
              )}
              <button
                onClick={handleClearFilters}
                className="font-extrabold text-rose-600 dark:text-rose-400 hover:underline ml-auto"
              >
                Clear
              </button>
            </div>
          )}

          {/* Student List Box */}
          <div className="flex flex-col">
            <div className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <FaUsers size={11} /> Select Student:
            </div>

            {loading ? (
              <div className="py-12 text-center text-xs font-bold text-indigo-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                Loading students...
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="py-12 text-center text-xs font-semibold text-gray-400 italic bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-gray-200 dark:border-slate-800">
                No students match your filter.
              </div>
            ) : (
              <div className="overflow-y-auto space-y-1.5 pr-1 custom-scrollbar max-h-[460px]">
                {filteredStudents.map((s) => {
                  const isSelected = selectedStudent?.id === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => handleStudentSelect(s)}
                      className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left transition border ${
                        isSelected
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-xs"
                          : "bg-gray-50 dark:bg-slate-800/80 text-gray-800 dark:text-slate-100 border-gray-200 dark:border-slate-700/80 hover:border-indigo-300 dark:hover:border-indigo-500"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-slate-700 flex items-center justify-center overflow-hidden shrink-0 border border-white/40">
                          {s.studentPhoto ? (
                            <img
                              src={
                                s.studentPhoto.startsWith("http")
                                  ? s.studentPhoto
                                  : `${server}/${s.studentPhoto.replace(/^\/+/, "")}`
                              }
                              alt={s.studentName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <FaUserGraduate className="text-xs text-indigo-600 dark:text-indigo-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-black truncate leading-tight">
                            {s.studentName}
                          </div>
                          <div
                            className={`text-[10px] font-bold truncate ${
                              isSelected ? "text-indigo-100" : "text-gray-500 dark:text-slate-400"
                            }`}
                          >
                            Class {s.class}-{s.section} | Reg: {s.admissionNumber}
                          </div>
                        </div>
                      </div>

                      {isSelected && (
                        <FaCheckCircle size={12} className="text-white shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN CARD: Preview Canvas & Adjustment Toolbar */}
        <div className="lg:col-span-7 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs flex flex-col items-center min-h-[660px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-indigo-600 font-bold animate-pulse">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-3"></div>
              Loading ID Card...
            </div>
          ) : !selectedStudent ? (
            <div className="print:hidden w-full max-w-sm rounded-3xl border-2 border-dashed border-gray-300 dark:border-slate-800 p-12 flex flex-col items-center justify-center text-gray-400 opacity-60 my-auto">
              <FaIdCard className="text-6xl mb-4" />
              <p className="font-semibold text-center text-xs">Select a student from the left panel</p>
            </div>
          ) : (
            <div
              id="id-card-print-area"
              className="w-full flex flex-col items-center pb-4"
            >
              {/* Photo & Navigation Control Toolbar (Hidden on Print) */}
              <div className="print:hidden mb-4 bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/80 p-3 rounded-2xl shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs w-[360px]">
                {/* Previous/Next Student Switcher */}
                <div className="flex items-center border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800">
                  <button
                    type="button"
                    onClick={handlePrevStudent}
                    disabled={filteredStudents.length <= 1}
                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 transition text-gray-700 dark:text-slate-200"
                    title="Previous Student"
                  >
                    <FaChevronLeft size={10} />
                  </button>
                  <span className="px-2 text-[10px] font-black text-gray-600 dark:text-slate-300 border-x border-gray-200 dark:border-slate-700">
                    {filteredStudents.findIndex((s) => s.id === selectedStudent?.id) + 1} / {filteredStudents.length}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextStudent}
                    disabled={filteredStudents.length <= 1}
                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 transition text-gray-700 dark:text-slate-200"
                    title="Next Student"
                  >
                    <FaChevronRight size={10} />
                  </button>
                </div>

                {/* Photo Zoom Controls */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPhotoScale((s) => Math.max(0.5, s - 0.1))}
                    className="p-1.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-indigo-100 hover:text-indigo-600 transition"
                    title="Zoom Out"
                  >
                    <FaSearchMinus size={11} />
                  </button>

                  <input
                    type="range"
                    min="0.5"
                    max="2.5"
                    step="0.05"
                    value={photoScale}
                    onChange={(e) => setPhotoScale(parseFloat(e.target.value))}
                    className="w-14 accent-indigo-600 cursor-pointer"
                    title="Zoom level"
                  />

                  <button
                    type="button"
                    onClick={() => setPhotoScale((s) => Math.min(2.5, s + 0.1))}
                    className="p-1.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-indigo-100 hover:text-indigo-600 transition"
                    title="Zoom In"
                  >
                    <FaSearchPlus size={11} />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setPhotoScale(1);
                      setPhotoPos({ x: 0, y: 0 });
                    }}
                    className="px-2 py-1 bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400 font-bold rounded-lg border border-rose-200 dark:border-rose-900 hover:bg-rose-100 transition flex items-center gap-1 text-[10px]"
                    title="Reset scale and position"
                  >
                    <FaUndo size={9} /> Reset
                  </button>
                </div>
              </div>

              {/* The Physical Card Container */}
              <div
                ref={componentRef}
                className="w-[360px] h-[580px] bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col shrink-0 relative z-0"
                style={{ border: "2px solid #e5e7eb" }}
              >
                {/* Background Watermark/Pattern */}
                <div className="absolute inset-0 pointer-events-none opacity-[0.03] flex items-center justify-center z-0">
                  <FaIdCard className="text-[300px]" />
                </div>

                {/* Decorative Geometric Top Shape using SVG clips or borders */}
                <div className="relative h-[160px] bg-gradient-to-br from-blue-700 via-indigo-800 to-indigo-900 flex flex-col items-center justify-start pt-4 z-10 clip-path-header">
                  {/* Lanyard Punch Hole Placeholder */}
                  <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-16 h-3 border border-indigo-500 rounded-full bg-indigo-900/50 flex shadow-inner overflow-hidden">
                    <div className="w-full h-full bg-gray-100/10"></div>
                  </div>

                  <div className="flex flex-col items-center mt-3 tracking-widest leading-tight">
                    <img
                      src="/assets/images/logo.jpg"
                      alt="Logo"
                      className="h-[40px] w-auto object-contain rounded shadow-md bg-white p-0.5 mb-1.5"
                    />
                    <h2 className="text-white font-black text-[17px] uppercase tracking-[0.1em] drop-shadow-md">
                      Dee Novo School
                    </h2>
                  </div>

                  {/* Angled cutoff effect using an absolute element */}
                  <svg
                    className="absolute bottom-0 w-full h-[36px] text-white"
                    preserveAspectRatio="none"
                    viewBox="0 0 100 100"
                    fill="currentColor"
                  >
                    <polygon points="0,100 100,0 100,100" />
                  </svg>
                  <svg
                    className="absolute bottom-0 w-full h-[36px] text-blue-500/20"
                    preserveAspectRatio="none"
                    viewBox="0 0 100 100"
                    fill="currentColor"
                  >
                    <polygon points="0,100 0,0 100,100" />
                  </svg>
                </div>

                {/* Central Content Area */}
                <div className="flex-1 flex flex-col items-center px-6 relative z-20">
                  {/* Outstanding Profile Picture (Interactive Drag & Zoom) */}
                  <div
                    className="w-28 h-28 -mt-[56px] rounded-full border-[5px] border-white shadow-lg bg-gray-50 flex items-center justify-center overflow-hidden shrink-0 relative z-20 select-none cursor-grab active:cursor-grabbing"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleMouseUp}
                    title="Click and drag to position student photo"
                  >
                    {selectedStudent.studentPhoto ? (
                      <img
                        src={
                          selectedStudent.studentPhoto.startsWith("http")
                            ? selectedStudent.studentPhoto
                            : `${server}/${selectedStudent.studentPhoto.replace(/^\/+/, "")}`
                        }
                        alt={selectedStudent.studentName || "Student"}
                        className="w-full h-full object-cover pointer-events-none"
                        style={{
                          transform: `translate(${photoPos.x}px, ${photoPos.y}px) scale(${photoScale})`,
                          transition: isDragging ? "none" : "transform 0.05s ease-out",
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = "block";
                          }
                        }}
                      />
                    ) : null}
                    <FaUserGraduate
                      className="text-6xl text-gray-300"
                      style={{
                        display: !selectedStudent.studentPhoto ? "block" : "none",
                      }}
                    />
                  </div>

                  {/* Identity Badges */}
                  <div className="w-full text-center mt-2">
                    <h3 className="text-[20px] font-black text-gray-900 uppercase tracking-tight leading-tight px-2 w-full truncate">
                      {selectedStudent.studentName}
                    </h3>
                    <div className="mt-1 flex justify-center">
                      <span className="bg-indigo-100 text-indigo-800 border border-indigo-200 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
                        STUDENT
                      </span>
                    </div>
                  </div>

                  {/* Information Grid Container */}
                  <div className="w-full mt-4 flex flex-col space-y-1.5 px-3 py-3 bg-indigo-50/40 rounded-xl border border-indigo-100/50 shadow-inner">
                    <div className="flex items-center text-[10px]">
                      <span className="font-bold text-indigo-800 uppercase tracking-wider w-24 shrink-0">
                        Class & Roll
                      </span>
                      <span className="font-black text-gray-800 truncate">
                        : {selectedStudent.class} {selectedStudent.section}{" "}
                        <span className="text-gray-300 mx-1">|</span>{" "}
                        {selectedStudent.rollNumber || "N/A"}
                      </span>
                    </div>

                    <div className="flex items-center text-[10px]">
                      <span className="font-bold text-indigo-800 uppercase tracking-wider w-24 shrink-0">
                        D.O.B
                      </span>
                      <span className="font-black text-gray-800 truncate">
                        : {formatDOB(selectedStudent.dateOfBirth)}
                      </span>
                    </div>

                    <div className="flex items-center text-[10px]">
                      <span className="font-bold text-indigo-800 uppercase tracking-wider w-24 shrink-0">
                        Father's Name
                      </span>
                      <span className="font-black text-gray-800 truncate">
                        : {selectedStudent.fatherName || "N/A"}
                      </span>
                    </div>

                    <div className="flex items-center text-[10px]">
                      <span className="font-bold text-indigo-800 uppercase tracking-wider w-24 shrink-0">
                        Admission No
                      </span>
                      <span className="font-black text-gray-800 truncate">
                        : {selectedStudent.admissionNumber || "N/A"}
                      </span>
                    </div>

                    <div className="flex items-center text-[10px]">
                      <span className="font-bold text-indigo-800 uppercase tracking-wider w-24 shrink-0">
                        Session
                      </span>
                      <span className="font-black text-gray-800 truncate">
                        : {selectedStudent.session || "N/A"}
                      </span>
                    </div>

                    <div className="flex items-center text-[10px]">
                      <span className="font-bold text-indigo-800 uppercase tracking-wider w-24 shrink-0">
                        Contact
                      </span>
                      <span className="font-black text-gray-800 truncate">
                        :{" "}
                        {selectedStudent.parentContactNo ||
                          selectedStudent.fatherMobileNo ||
                          "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bottom Footer Section */}
                <div className="h-[80px] w-full mt-auto relative z-10 flex flex-col items-center shrink-0">
                  <div className="w-full flex justify-between items-end px-8 pb-3">
                    {/* Dynamic QR Code */}
                    <div className="flex flex-col items-center opacity-90">
                      <div className="bg-white p-[2px] rounded border border-gray-200 shadow-sm">
                        <QRCode
                          value={`${qrBaseUrl}/verify/idCard/${selectedStudent.uniqueId || ""}`}
                          size={36}
                          level="M"
                        />
                      </div>
                      <span className="text-[6px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                        Verify
                      </span>
                    </div>

                    {/* Principal Signature Area */}
                    <div className="flex flex-col items-center">
                      <div className="font-[cursive] text-xls text-indigo-900/80 -rotate-3 mb-1">
                        Signature
                      </div>
                      <div className="w-20 h-[1px] bg-gray-400"></div>
                      <span className="text-[7px] font-bold text-gray-500 uppercase tracking-widest mt-1">
                        Principal
                      </span>
                    </div>
                  </div>

                  {/* Absolute Bottom Strip */}
                  <div className="w-full bg-indigo-900 flex items-center justify-between px-6 py-2" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
